import React, { useEffect, useState } from 'react';
import {
  DEM_PRODUCT_LABELS, ELEVATION_UNITS, availableDemProducts, canBeDem, demInfoRows, isDem, niceInterval, terrainApi,
  terrainJobState,
} from '../../lib/terrain-api';
import { setTerrainSettings, getTerrainSettings } from '../../lib/terrain-settings';

/**
 * DEM block of the RasterCard (docs/DEM_TERRAIN.md, section 7): mark a single-band raster
 * as a DEM (units, vertical datum), DEM facts, "Use as project DEM", the terrain-analysis
 * form (slope, aspect, hillshade, contours) and buttons that open each product on the map.
 *
 * Props: dataset, onShowOnMap({ dem: product }), onChanged() after a change worth a reload.
 */
const ANALYSIS = ['slope', 'aspect', 'hillshade', 'contours'];

const errText = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

export default function DemPanel({ dataset, onShowOnMap, onChanged }) {
  const meta = dataset?.metadata;
  const raster = meta?.raster;
  const dem = isDem(meta);
  const d = raster?.dem || {};
  const userDem = meta?.raster_source?.dem || {};
  const [units, setUnits] = useState(userDem.elevation_units || d.elevation_units || 'm');
  const [datum, setDatum] = useState(userDem.vertical_datum || d.vertical_datum || '');
  const span = Number.isFinite(d.max) && Number.isFinite(d.min) ? d.max - d.min : 100;
  const [interval, setContourInterval] = useState(String(niceInterval(span)));
  const [products, setProducts] = useState(ANALYSIS);
  const [azimuth, setAzimuth] = useState('315');
  const [altitude, setAltitude] = useState('45');
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  useEffect(() => { setContourInterval(String(niceInterval(span))); }, [span]);

  if (!canBeDem(dataset) || !raster) return null;
  const job = terrainJobState(meta);
  const projectDem = getTerrainSettings().demDatasetId === dataset.id;

  const act = async (label, fn) => {
    setBusy(label);
    setMessage(null);
    try {
      const msg = await fn();
      if (msg) setMessage({ ok: true, text: msg });
      if (onChanged) onChanged();
    } catch (e) {
      setMessage({ ok: false, text: errText(e, `${label} failed`) });
    } finally {
      setBusy(null);
    }
  };

  const track = async (res, label) => {
    if (res?.data?.job) {
      const { trackJob } = await import('../../lib/jobs-api');
      trackJob(res.data.job, { label }).then(() => onChanged && onChanged()).catch(() => {});
    }
  };

  const saveDem = (isDemNow) => act('Saving', async () => {
    const res = await terrainApi.setDem(dataset.id, { is_dem: isDemNow, elevation_units: units, vertical_datum: datum || null });
    await track(res, `DEM: ${dataset.name || dataset.id}`);
    return res.status === 202 ? 'Reprocessing the raster as a DEM' : 'Saved';
  });

  const useForProject = () => act('Linking', async () => {
    const res = await terrainApi.setProjectTerrain(dataset.project_id, dataset.id);
    setTerrainSettings({ projectId: dataset.project_id, demDatasetId: dataset.id, dem: res.data?.dem || null });
    return 'This DEM is now the project terrain (sections, profiles, 3D)';
  });

  const analyze = () => act('Analysis', async () => {
    const body = { products, azimuth: Number(azimuth), altitude: Number(altitude) };
    if (products.includes('contours')) body.contour_interval = Number(interval);
    const res = await terrainApi.analyze(dataset.id, body);
    await track(res, `Terrain: ${dataset.name || dataset.id}`);
    return 'Terrain analysis queued; progress is in the task console';
  });

  const input = 'px-2 py-1 text-xs border border-border rounded-md bg-background';
  const button = 'px-3 py-1 text-xs border border-border rounded-md hover:bg-muted disabled:opacity-50';

  return (
    <div className="mt-6 border-t border-border pt-4" data-testid="dem-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold">{dem ? 'DEM terrain' : 'Elevation model'}</h3>
        {dem && dataset.project_id && (
          <button className={button} onClick={useForProject} disabled={!!busy || projectDem} data-testid="dem-use-project">
            {projectDem ? 'Project DEM' : 'Use as project DEM'}
          </button>
        )}
      </div>

      {dem && (
        <dl className="grid grid-cols-2 gap-4 mb-3">
          {demInfoRows(raster).map(([label, value]) => (
            <div key={label}>
              <dt className="text-sm font-medium text-muted-foreground mb-1">{label}</dt>
              <dd className="text-sm font-semibold">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm mb-3">
        <label htmlFor="dem-units" className="text-muted-foreground">Units</label>
        <select id="dem-units" value={units} onChange={(e) => setUnits(e.target.value)} className={input}>
          {ELEVATION_UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label htmlFor="dem-datum" className="text-muted-foreground">Vertical datum</label>
        <input id="dem-datum" value={datum} onChange={(e) => setDatum(e.target.value)} placeholder="e.g. NAVD88"
          className={`${input} w-28`} />
        <button className={button} disabled={!!busy} onClick={() => saveDem(true)} data-testid="dem-save">
          {dem ? 'Save' : 'Mark as DEM'}
        </button>
        {dem && (
          <button className={button} disabled={!!busy} onClick={() => saveDem(false)}>Not a DEM</button>
        )}
      </div>

      {dem && (
        <>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="dem-products">
            {availableDemProducts(meta).map((p) => (
              <button key={p} className={button} onClick={() => onShowOnMap && onShowOnMap({ dem: p })}>
                {DEM_PRODUCT_LABELS[p]} on map
              </button>
            ))}
          </div>

          <div className="space-y-2 text-sm" data-testid="dem-analysis">
            <div className="flex flex-wrap gap-3">
              {ANALYSIS.map((p) => (
                <label key={p} className="flex items-center gap-1">
                  <input type="checkbox" checked={products.includes(p)}
                    onChange={() => setProducts((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]))} />
                  {DEM_PRODUCT_LABELS[p]}
                </label>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-muted-foreground" htmlFor="dem-interval">Contour interval ({d.elevation_units || 'm'})</label>
              <input id="dem-interval" value={interval} onChange={(e) => setContourInterval(e.target.value)} className={`${input} w-20`} />
              <label className="text-muted-foreground" htmlFor="dem-az">Sun azimuth</label>
              <input id="dem-az" value={azimuth} onChange={(e) => setAzimuth(e.target.value)} className={`${input} w-16`} />
              <label className="text-muted-foreground" htmlFor="dem-alt">altitude</label>
              <input id="dem-alt" value={altitude} onChange={(e) => setAltitude(e.target.value)} className={`${input} w-16`} />
              <button className={button} onClick={analyze} data-testid="dem-analyze"
                disabled={!!busy || !products.length || job.state === 'processing'
                  || (products.includes('contours') && !(Number(interval) > 0))}>
                {job.state === 'processing' ? 'Analysing…' : 'Run terrain analysis'}
              </button>
            </div>
            {job.state === 'failed' && <p className="text-xs text-red-800 bg-red-100 rounded p-2" role="alert">{job.error}</p>}
          </div>
        </>
      )}

      {message && (
        <p className={`text-xs mt-2 ${message.ok ? 'text-muted-foreground' : 'text-red-700'}`} role={message.ok ? 'status' : 'alert'}>
          {message.text}
        </p>
      )}
    </div>
  );
}
